// hooks/themeContextMode.js
import { useEffect, useState, useCallback } from "react";
 
const DARK = "dark-mode";
const LIGHT = "light-mode";
const STORAGE_KEY = "darkMode";
const prefersDarkQuery = "(prefers-color-scheme: dark)";
 
const getSystemPreference = () => {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  return window.matchMedia(prefersDarkQuery).matches;
};
 
const getInitialTheme = () => {
  if (typeof window === "undefined") return DARK; // SSR-safe default
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored !== null) return stored === "true" ? DARK : LIGHT;
  return getSystemPreference() ? DARK : LIGHT;
};
 
const useThemeMode = ({
  classNameDark = DARK,
  classNameLight = LIGHT,
  element = typeof document !== "undefined" ? document.body : null,
} = {}) => {
  const [theme, setTheme] = useState(getInitialTheme);
 
  // Apply theme to DOM and persist
  useEffect(() => {
    if (!element) return;
 
    const isDark = theme === classNameDark;
    element.classList.remove(classNameDark, classNameLight);
    element.classList.add(isDark ? classNameDark : classNameLight);
    localStorage.setItem(STORAGE_KEY, isDark.toString());
  }, [theme, element, classNameDark, classNameLight]);
 
  // Watch system preference changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
 
    const mediaQuery = window.matchMedia(prefersDarkQuery);
    const handleChange = (e) => {
      const newTheme = e.matches ? classNameDark : classNameLight;
      setTheme(newTheme);
    };
 
    mediaQuery.addEventListener?.("change", handleChange);
    return () => {
      mediaQuery.removeEventListener?.("change", handleChange);
    };
  }, [classNameDark, classNameLight]);
 
  const enable = useCallback(() => setTheme(classNameDark), [classNameDark]);
  const disable = useCallback(() => setTheme(classNameLight), [classNameLight]);
  const toggle = useCallback(() => {
    setTheme((prev) =>
      prev === classNameDark ? classNameLight : classNameDark
    );
  }, [classNameDark, classNameLight]);
 
  return {
    theme,
    enable,
    disable,
    toggle,
    isDark: theme === classNameDark,
  };
};
 
export default useThemeMode;
 